import { GridOptions } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import type { TemplateSampleModuleData } from 'src/AngularJS/Dashboard/Modules/ts-template.model';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { NgTemplateService } from './ng-template.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'app-ng-template',
    templateUrl: './ng-template.component.html',
    styleUrls: ['./ng-template.component.scss']
})
export class NgTemplateComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('ngTemplateGrid') agGrid: AgGridAngular;

    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
    private localizationStrings: Map<string, string> = new Map<string, string>(); // This is used for localizations.
    private stateSaveTimer: number;

    public data: TemplateSampleModuleData = null; // A copy of data returned by loadData will be stored here.
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;
    // Ag-grid configuration:
    public gridOptions: GridOptions = null;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService, // Used to pull translations.  Most strings should be localized in the markup aspx file, but there are some strings (like AgGrid) that need to be localized in the controller code.
        private ngTempalteService: NgTemplateService,
    ) { }

    /**
     * Loads the localizations for this module, then sets up the grid.
     */
    private async loadLocalizations() {
        try {
            this.localizationStrings = await this.cvLocalizationService.GetSection("Template_Module");
        } finally {
            this.setupGrid();
        }
    }

    /**
     * Returns the localization for the provided Key, or defaultValue if missing.
     * @param key String's Key from the *.Language Section for this module
     * @param defaultValue String returned if localizations didn't load, or the key is missing.
     */
    private getLocalizationString(key: string, defaultValue: string): string {
        if (this.localizationStrings?.has(key))
            return this.localizationStrings.get(key);
        return defaultValue;
    }

    /**
     * Sets up the grid options.  Should be called after localizations are loaded, because the column headers are localized.
     */
    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({ // Please use AgGridHelperService.  This way your grid options will inherit logic all modules should have.  Properties you define in this object will replace the ones from the factory.
            columnDefs: [
                {
                    field: "ID", // This is the key name from object representing this row's data.  rowData is an array of objects.
                    colId: "ID", // Specifying a column ID is optional, but if you change the fields, saved state won't match later.
                    headerName: this.getLocalizationString("Identification_Header", "Identification"), // Header Name can be a custom string for display.  Consider localization.
                    filter: "agNumberColumnFilter",
                },
                {
                    field: "Message",
                    colId: "Message",
                    headerName: this.getLocalizationString("Message_Header", "Sample mEsSaGeS"),
                    filter: "agTextColumnFilter",
                },
            ],
            onGridReady: params => {
                if (this.data?.RowValues) {
                    params.api.setGridOption('rowData', this.data.RowValues);
                }
                if (this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
                this.applyFilter();
            },

            // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
            // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display.
            getRowId: ({ data: rowData }) => rowData.ID,

            // The filter can also be set with the setQuickFilter() api call on the grid.
            // isExternalFilterPresent: () => this.filterValue.length > 0, // External Filter refers to our search bar.  This function returns true when $ctrl.filterValue has content, false when otherwise.  False causes ag-grid to show all row values.
            // doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue), // This function actually uses the value of the search bar, and it compares it to the data of the row ag-grid is passing in (node).  Use agGridHelperFactory's unless you have good reason.
            onSortChanged: event => this.queueStateSave(),
            onColumnMoved: event => this.queueStateSave(),
            onColumnPinned: event => this.queueStateSave(),
            onColumnVisible: event => {
                event.api.sizeColumnsToFit();
                this.queueStateSave();
            },
            // rowData: []
        });
    }

    // Called by AngularJS when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            // this.agGrid?.api.onFilterChanged(); // Used with the External Filter.
            this.filterValue = this.filterValue.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    }

    // Example loadData function:
    private async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.ngTempalteService.loadTemplateSampleData(this.moduleKey, isAutoRefresh, this.isFirstLoad);

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data.RowValues);
            }
            // else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.

            this.moduleManager.showLoader = false;
            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
        }
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        // Bound values ("@Input()" above) are only valid in and after this function.
        // Show the loading spinner.  This will be unset in the loadData() function:
        this.moduleManager.showLoader = true;
        this.loadLocalizations();

        // Module Configuration:
        this.moduleManager.setOptions({
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: settings => {
                // this function will be called when the settings are loaded for the first time (for existing modules)
                //  and also when the user clicks the apply button in the settings modal

                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true, // hasRefresh determines if a module has a refresh menu item, this item will call onRefresh when clicked
            // in general refreshType should not be changed, it will be determined automatically based upon metrics you have.
            // if it does need to be changed, see Module Container's moduleOptions object for more information
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                // isAutoRefresh is set to true when the timer is causing the reload.  It's false when the user clicks the refresh button themself.
                // this function will be called when the refresh interval for this module is hit
                // this can be called before the module is ready for it, so any checking to determine if
                //  it is actually ready to refresh should be done here
                // example:
                // if (this.dataLoaded)
                //    loadData(); // load data again
                // note: loadData() should have code to make sure we don't show a loading animation and instead just update the data we already have
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
        });

        //#region Menu Items

        // Example menu item, can be removed
        // See Module Container's addMenuItem() for more on menu items
        this.moduleManager.addMenuItem({
            name: "Click Me!", // If this value exists as a key in *.language [Module_Menu] section, it should get localized automatically.
            icon: "refresh",
            click: () => {
                console.log("I was clicked.");
                this.loadData(false);
            },
        });

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        //#endregion

        //#region Event Handlers
        // Example code for gaining/losing focus, can be removed
        // See Module Container's registerEvents() for more on event handlers
        this.moduleManager.registerEvents({
            onGainedFocus: () => {
                // this.enableAutoRefresh = true;
            },
            onLostFocus: () => {
                // this.enableAutoRefresh = false;
            }
        });
        //#endregion

        //#region States and Settings
        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: stateData => {
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });
        // Settings - used for adding custom settings to the settings dialog
        //  requires a [Template]Settings directive (see _TemplateSettings.js/aspx)
        //#endregion
    }

}
